import React from "react";
import { OpenFrame, OpenTimeSeries } from "@captor/openseries-ts";
import { numberFormatFun } from "../../../common/src/utils/FormatFunctions";

const format = numberFormatFun("0.0%");
const integerFormat = numberFormatFun("0.00");

type CompareMetricsTableProps = {
    timeseries: OpenTimeSeries[];
    /** Index into `timeseries` used as the reference for relative metrics. Defaults to 0. */
    referenceColumn?: number;
};

function safeNum(fn: () => number): number {
    try {
        const v = fn();
        return typeof v === "number" ? v : Number.NaN;
    } catch {
        return Number.NaN;
    }
}

export const CompareMetricsTable = ({ timeseries, referenceColumn = 0 }: CompareMetricsTableProps): React.ReactElement => {
    const valid = timeseries?.filter((ts) => ts && ts.length > 0) ?? [];
    if (valid.length === 0) return <div />;

    const frame = valid.length >= 2 ? new OpenFrame(valid).mergeSeries("inner") : null;
    const requestedRef =
        timeseries && timeseries[referenceColumn] && timeseries[referenceColumn].length > 0 ? timeseries[referenceColumn] : null;
    let benchmarkCol = requestedRef ? valid.findIndex((ts) => ts === requestedRef) : 0;
    if (benchmarkCol < 0) benchmarkCol = 0;

    const startDates = valid.map((ts) => ts.firstIdx ?? "-");
    const endDates = valid.map((ts) => ts.lastIdx ?? "-");

    const lastDates = valid.map((ts) => ts.getTsdfDates()[ts.length - 1]);
    const ytdReturns = valid.map((ts, i) => {
        const lastDate = lastDates[i];
        if (!lastDate || ts.length < 2) return NaN;
        const d = new Date(lastDate);
        const ytdFrom = `${d.getFullYear()}-01-01`;
        return safeNum(() => ts.valueRet({ fromDate: ytdFrom, toDate: lastDate }));
    });
    const mtdReturns = valid.map((ts, i) => {
        const lastDate = lastDates[i];
        if (!lastDate || ts.length < 2) return NaN;
        const d = new Date(lastDate);
        const mtdFrom = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-01`;
        return safeNum(() => ts.valueRet({ fromDate: mtdFrom, toDate: lastDate }));
    });

    const hasMultiple = valid.length >= 2;
    const infoRatios = hasMultiple && frame
        ? valid.map((ts, i) => {
              if (i === benchmarkCol) return "-";
              try {
                  const ratios = frame.infoRatio(benchmarkCol);
                  return ratios[i] != null ? integerFormat(ratios[i]) : "-";
              } catch {
                  return "-";
              }
          })
        : [];
    const trackingErrors = hasMultiple && frame
        ? valid.map((ts, i) => {
              if (i === benchmarkCol) return "-";
              try {
                  const errs = frame.trackingError(benchmarkCol);
                  return errs[i] != null ? format(errs[i]) : "-";
              } catch {
                  return "-";
              }
          })
        : [];
    const betas = hasMultiple && frame
        ? valid.map((ts, i) => {
              if (i === benchmarkCol) return "-";
              try {
                  return integerFormat(frame.beta(i, benchmarkCol));
              } catch {
                  return "-";
              }
          })
        : [];

    const rows: { header: string; values: (string | number)[]; format?: "percent" | "number" }[] = [
        { header: "Return (CAGR)", values: valid.map((ts) => safeNum(() => ts.geoRet())), format: "percent" },
        { header: "Year-to-Date", values: ytdReturns, format: "percent" },
        { header: "Month-to-Date", values: mtdReturns, format: "percent" },
        { header: "Volatility", values: valid.map((ts) => safeNum(() => ts.vol())), format: "percent" },
        { header: "Sharpe Ratio", values: valid.map((ts) => safeNum(() => ts.retVolRatio())), format: "number" },
        { header: "Sortino Ratio", values: valid.map((ts) => safeNum(() => ts.sortinoRatio(0, 0))), format: "number" },
        { header: "Total Return", values: valid.map((ts) => safeNum(() => ts.valueRet())), format: "percent" },
        { header: "Max Drawdown", values: valid.map((ts) => safeNum(() => ts.maxDrawdown())), format: "percent" },
        { header: "Worst Month", values: valid.map((ts) => safeNum(() => ts.worstMonth())), format: "percent" },
        { header: "Pos. Share", values: valid.map((ts) => safeNum(() => ts.positiveShare())), format: "percent" },
        { header: "Comparison Start", values: startDates },
        { header: "Comparison End", values: endDates }
    ];

    if (hasMultiple) {
        rows.push(
            {
                header: "Jensen's Alpha",
                values: valid.map((_, i) => (i === benchmarkCol ? "—" : (frame ? integerFormat(frame.jensenAlpha(i, benchmarkCol)) : "—"))),
                format: "number"
            },
            { header: "Information Ratio", values: infoRatios, format: "number" },
            { header: "Tracking Error", values: trackingErrors, format: "percent" },
            { header: "Index Beta", values: betas, format: "number" },
            {
                header: "Capture Ratio",
                values: valid.map((_, i) =>
                    i === benchmarkCol ? "—" : (frame ? integerFormat(frame.captureRatio("both", benchmarkCol)[i]) : "—")
                ),
                format: "number"
            }
        );
    }

    const fmt = (v: string | number, f?: string) => {
        if (v === "—" || (typeof v === "string" && v.includes("—"))) return v;
        if (typeof v === "string") return v;
        if (!isFinite(v as number) || (typeof v === "number" && isNaN(v))) return "—";
        if (f === "percent") return format(v as number);
        if (f === "number") return integerFormat(v as number);
        return String(v);
    };

    const name = (ts: OpenTimeSeries) => ts.label || ts.name || "";

    return (
        <div className="my-3.5">
            <table className="w-full text-sm border-collapse">
                <thead>
                    <tr className="bg-white">
                        <th className="py-1.5 px-2.5 text-left font-bold">Metric</th>
                        {valid.map((ts, i) => (
                            <th
                                key={i}
                                className="py-1.5 px-2.5 text-left font-bold align-bottom whitespace-normal break-words leading-snug"
                            >
                                {name(ts)}
                            </th>
                        ))}
                    </tr>
                </thead>
                <tbody>
                    {rows.map((row, rowIdx) => (
                        <tr key={rowIdx} className={rowIdx % 2 === 0 ? "bg-gray-100" : "bg-white"}>
                            <td className="py-1.5 px-2.5 font-bold">{row.header}</td>
                            {row.values.map((val, colIdx) => (
                                <td key={colIdx} className="py-1.5 px-2.5">
                                    {fmt(val, row.format)}
                                </td>
                            ))}
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    );
};
